import { useState, FormEvent, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useCart } from '@/hooks/useCart';

const CHECKOUT_FORM_URL = 'https://readdy.ai/api/form/d75nk8d4hjod66j2eci0';

export default function CartPage() {
  const { items, removeItem, updateQuantity, clearCart, totalItems, totalPrice } = useCart();
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const orderSummary = useMemo(
    () =>
      items
        .map((i) => `${i.name} x${i.quantity} ($${(i.price * i.quantity).toLocaleString()})`)
        .join('; '),
    [items]
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (items.length === 0) return;
    const form = e.currentTarget;
    const notes = (form.elements.namedItem('notes') as HTMLTextAreaElement)?.value || '';
    if (notes.length > 500) return;

    setStatus('sending');
    try {
      const formData = new FormData(form);
      formData.set('orderSummary', orderSummary);
      formData.set('orderTotal', `$${totalPrice.toLocaleString()}`);
      const body = new URLSearchParams(formData as unknown as URLSearchParams).toString();
      const res = await fetch(CHECKOUT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (res.ok) {
        setStatus('success');
        clearCart();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div>
        <Navbar />
        <section className="w-full min-h-[80vh] bg-[#faf8f5] flex flex-col items-center justify-center px-6 py-24">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 flex items-center justify-center bg-[#f0e6d8] rounded-full mx-auto mb-6">
              <i className="ri-check-double-line text-3xl text-[#8B6348]" />
            </div>
            <h2 className="text-3xl font-bold text-[#2c2420] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Order Submitted!
            </h2>
            <p className="text-stone-500 text-base leading-relaxed mb-8">
              Thank you for your order. The SWIFTMARKET LLC team will review your request and contact you within 24 hours to confirm details and arrange delivery.
            </p>
            <Link
              to="/products"
              className="whitespace-nowrap px-8 py-3.5 bg-[#2c2420] text-white text-sm font-semibold rounded-full hover:bg-[#8B6348] transition-colors cursor-pointer inline-block"
            >
              Continue Shopping
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar />

      <section className="w-full bg-[#f5f0ea] pt-36 pb-12 px-6 md:px-16">
        <p className="text-xs uppercase tracking-[0.3em] text-[#8B6348] mb-3 font-medium">
          {totalItems > 0 ? `${totalItems} Items` : 'Empty'}
        </p>
        <h1 className="text-5xl font-bold text-[#2c2420]" style={{ fontFamily: "'Playfair Display', serif" }}>
          Shopping Cart
        </h1>
      </section>

      {items.length === 0 ? (
        <section className="w-full bg-[#faf8f5] py-32 flex flex-col items-center justify-center px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-[#f0e6d8] rounded-full mb-5">
            <i className="ri-shopping-bag-line text-2xl text-[#8B6348]" />
          </div>
          <p className="text-xl font-semibold text-[#2c2420] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your cart is empty
          </p>
          <p className="text-stone-400 text-sm mb-8">Add some beautiful pieces to get started</p>
          <Link
            to="/products"
            className="whitespace-nowrap px-8 py-3.5 bg-[#2c2420] text-white text-sm font-semibold rounded-full hover:bg-[#8B6348] transition-colors cursor-pointer"
          >
            Browse Products
          </Link>
        </section>
      ) : (
        <section className="w-full bg-[#faf8f5] py-16 px-6 md:px-16">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-12 items-start">

            {/* Cart Items */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-[#2c2420]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Order Items
                </h2>
                <button
                  onClick={clearCart}
                  className="whitespace-nowrap text-xs text-stone-400 hover:text-red-400 transition-colors cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-delete-bin-line" />
                  Clear All
                </button>
              </div>

              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="bg-white rounded-2xl p-5 flex gap-5">
                    <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-0.5">{item.category}</p>
                          <h4 className="text-[#2c2420] font-semibold text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
                            {item.name}
                          </h4>
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-stone-300 hover:text-red-400 transition-colors cursor-pointer w-8 h-8 flex items-center justify-center flex-shrink-0"
                        >
                          <i className="ri-close-line text-lg" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-1 bg-[#faf8f5] rounded-full border border-stone-200 px-2 py-1">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-7 h-7 flex items-center justify-center text-stone-500 hover:text-[#8B6348] cursor-pointer transition-colors"
                          >
                            <i className="ri-subtract-line" />
                          </button>
                          <span className="text-[#2c2420] font-medium w-7 text-center text-sm">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-7 h-7 flex items-center justify-center text-stone-500 hover:text-[#8B6348] cursor-pointer transition-colors"
                          >
                            <i className="ri-add-line" />
                          </button>
                        </div>
                        <p className="text-[#8B6348] font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                          ${(item.price * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/products"
                className="whitespace-nowrap mt-6 inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#8B6348] transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                Continue Shopping
              </Link>
            </div>

            {/* Checkout Form */}
            <div className="w-full lg:w-[460px] flex-shrink-0">
              <div className="bg-white rounded-3xl p-8">
                <h2 className="text-xl font-bold text-[#2c2420] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Checkout Details
                </h2>

                {/* Order Summary */}
                <div className="bg-[#faf8f5] rounded-2xl p-5 mb-7">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs uppercase tracking-widest text-stone-400 font-medium">Order Summary</span>
                  </div>
                  <div className="space-y-2 mb-4">
                    {items.map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-stone-500 truncate mr-4">{item.name} × {item.quantity}</span>
                        <span className="text-[#2c2420] font-medium flex-shrink-0">${(item.price * item.quantity).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-stone-200 pt-3 flex justify-between">
                    <span className="text-[#2c2420] font-semibold text-sm">Total</span>
                    <span className="text-[#8B6348] font-bold text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                      ${totalPrice.toLocaleString()}
                    </span>
                  </div>
                </div>

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="checkout-form"
                  className="space-y-4"
                >
                  <input type="hidden" name="orderSummary" value={orderSummary} />
                  <input type="hidden" name="orderTotal" value={`$${totalPrice.toLocaleString()}`} />

                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-1.5">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        placeholder="John"
                        className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-1.5">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        placeholder="Smith"
                        className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-1.5">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+1 (602) 555-0000"
                      className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-1.5">
                      Delivery Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      required
                      placeholder="123 Main Street"
                      className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-1.5">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        required
                        placeholder="Phoenix"
                        className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300"
                      />
                    </div>
                    <div className="w-24">
                      <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-1.5">
                        State *
                      </label>
                      <input
                        type="text"
                        name="state"
                        required
                        placeholder="AZ"
                        maxLength={2}
                        className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300"
                      />
                    </div>
                    <div className="w-28">
                      <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-1.5">
                        ZIP *
                      </label>
                      <input
                        type="text"
                        name="zip"
                        required
                        placeholder="85017"
                        className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-1.5">
                      Order Notes
                    </label>
                    <textarea
                      name="notes"
                      rows={3}
                      maxLength={500}
                      placeholder="Any special requests or delivery instructions..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300 resize-none"
                    />
                    <p className={`text-xs mt-1 text-right ${charCount > 450 ? 'text-red-400' : 'text-stone-300'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  {status === 'error' && (
                    <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || charCount > 500}
                    className="whitespace-nowrap w-full py-4 bg-[#2c2420] text-white text-sm font-bold tracking-wider rounded-xl hover:bg-[#8B6348] transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {status === 'sending' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="ri-secure-payment-line" />
                        Place Order — ${totalPrice.toLocaleString()}
                      </>
                    )}
                  </button>
                  <p className="text-stone-400 text-xs text-center">
                    Our team will confirm your order within 24 hours
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
